import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Modal } from "../../components/tailwind/modal/Modal";
import {
  Button,
  Dropdown,
  ToggleCheckbox,
  ToggleLabel,
} from "../../components/tailwind";
import { useGetAuthQuery } from "../../api/authApi";
import { useGetCustomerQuery } from "../../api/customerApi";
import { useGetProductQuery } from "../../api/productDetailSlice";
import {
  setDjangoToastOpen,
  NotificationAppearance,
} from "../../api/djangoToastSlice";
import { renderLanguageFromCode } from "../../customers/customerlanguages";
import { ChannelLanguagePair, ProductId } from "../product";
import { RootState, store } from "../../utils/store";
import { sendProductToProofreading } from "./proofreadActions";

type Props = {
  dialogRef: React.RefObject<HTMLDialogElement>;
  productId: ProductId;
};

type PairKey = string;

const pairKey = (pair: ChannelLanguagePair): PairKey =>
  `${pair.channel_id}::${pair.language_code}`;

export const ProductProofreadRequestModal: React.FC<Props> = ({
  dialogRef,
  productId,
}) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const { data: auth, isLoading: isAuthLoading } = useGetAuthQuery();
  const { data: customer } = useGetCustomerQuery();
  const { data: product } = useGetProductQuery(productId);

  const [emailDelivery, setEmailDelivery] = useState("");
  const [sourceLanguage, setSourceLanguage] = useState("");
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [selectedPairKeys, setSelectedPairKeys] = useState<Set<PairKey>>(
    new Set()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availablePairs = useMemo<ChannelLanguagePair[]>(() => {
    if (!product) return [];
    if (product.channel_language_pairs.length > 0) {
      return product.channel_language_pairs;
    }
    if (!customer) return [];
    return customer.channel_language_pairs
      .filter((p) => p.default)
      .map((p) => new ChannelLanguagePair(p.language_code, p.channel_id));
  }, [product, customer]);

  useEffect(() => {
    if (!isAuthLoading && auth) {
      setEmailDelivery(auth.user.email);
    }
  }, [auth, isAuthLoading]);

  useEffect(() => {
    setSelectedPairKeys(new Set(availablePairs.map(pairKey)));
  }, [availablePairs]);

  const togglePair = (pair: ChannelLanguagePair): void => {
    setSelectedPairKeys((prev) => {
      const next = new Set(prev);
      const key = pairKey(pair);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const allSelected =
    availablePairs.length > 0 &&
    availablePairs.every((p) => selectedPairKeys.has(pairKey(p)));

  const toggleAll = (): void => {
    setSelectedPairKeys(
      new Set(allSelected ? [] : availablePairs.map(pairKey))
    );
  };

  const notify = (
    message: string,
    appearance: NotificationAppearance
  ): void => {
    store.dispatch(setDjangoToastOpen({ appearance, content: message }));
  };

  const onSubmit = async (): Promise<void> => {
    if (!token) return;
    const selectedPairs = availablePairs.filter((p) =>
      selectedPairKeys.has(pairKey(p))
    );
    setIsSubmitting(true);
    try {
      const result = await sendProductToProofreading(token, productId, {
        channel_language_pairs: selectedPairs.map((p) => ({
          channel_id: p.channel_id,
          language_code: p.language_code,
        })),
        email_delivery: emailDelivery,
        source_language: sourceLanguage,
        additional_instructions: additionalInstructions,
        is_urgent: isUrgent,
      });
      notify(result.message, NotificationAppearance.SUCCESS);
      dialogRef.current?.close();
      setAdditionalInstructions("");
      setSourceLanguage("");
      setIsUrgent(false);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to send proofreading request";
      notify(message, NotificationAppearance.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPairLabel = (pair: ChannelLanguagePair): string => {
    const channelName =
      customer?.getChannelById(pair.channel_id)?.display_name ??
      `Channel ${pair.channel_id}`;
    const languageName =
      renderLanguageFromCode(pair.language_code, customer?.languages ?? []) ??
      pair.language_code;
    return `${channelName} / ${languageName}`;
  };

  const canSubmit =
    selectedPairKeys.size > 0 &&
    emailDelivery.trim().length > 0 &&
    !isSubmitting;

  return (
    <Modal dialogRef={dialogRef}>
      <Modal.Header divide className="!tw-py-3">
        <h2
          className="!tw-font-semibold !tw-mb-0"
          data-testid="proofread-modal-title"
        >
          Send to proofreading
        </h2>
      </Modal.Header>
      <Modal.Body>
        <form method="dialog" className="txu-form tw-flex tw-flex-col tw-gap-4">
          <div>
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
              <label className="!tw-mb-0">Channels and languages *</label>
              {availablePairs.length > 0 && (
                <Button
                  type="button"
                  onClick={toggleAll}
                  data-testid="proofread-modal-toggle-all"
                >
                  {allSelected ? "Clear all" : "Select all"}
                </Button>
              )}
            </div>
            {availablePairs.length === 0 ? (
              <small>
                No channel/language pairs available for this product.
              </small>
            ) : (
              <div className="tw-flex tw-flex-col tw-gap-1 tw-max-h-56 tw-overflow-y-auto tw-ring-1 tw-ring-gray-300 tw-rounded-md tw-p-2">
                {availablePairs.map((pair) => {
                  const key = pairKey(pair);
                  return (
                    <label
                      key={key}
                      className="tw-flex tw-items-center tw-gap-2 !tw-mb-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPairKeys.has(key)}
                        onChange={(): void => togglePair(pair)}
                        data-testid={`proofread-modal-pair-${key}`}
                        className="tw-mx-2"
                      />
                      <span>{renderPairLabel(pair)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="!tw-mb-0" htmlFor="proofread-source-language">
              Source language
            </label>
            <small>
              Added as a reference column in the document list so the
              proofreader can compare against it.
            </small>
            <Dropdown<string>
              control="search"
              clearable
              placeholder="Select source language"
              testIdBase="proofread-modal-source-language"
              value={sourceLanguage}
              onChange={(value): void => setSourceLanguage(value as string)}
              options={(customer?.languages ?? []).map((language) => ({
                key: language.code,
                value: language.code,
                text: `${language.flag} ${language.short_name}`,
              }))}
            />
          </div>
          <div>
            <label className="!tw-mb-0" htmlFor="proofread-delivery">
              Deliver finished texts to *
            </label>
            <input
              id="proofread-delivery"
              type="email"
              className="tw-w-full"
              value={emailDelivery}
              onChange={(e): void => setEmailDelivery(e.target.value)}
              data-testid="proofread-modal-delivery"
            />
          </div>
          <div>
            <label htmlFor="proofread-instructions">
              Additional instructions
            </label>
            <textarea
              id="proofread-instructions"
              className="tw-w-full"
              value={additionalInstructions}
              onChange={(e): void => setAdditionalInstructions(e.target.value)}
              data-testid="proofread-modal-instructions"
            />
          </div>
          <div className="tw-flex tw-gap-2 tw-items-center [&>*]:!tw-mb-0">
            <ToggleCheckbox
              checked={isUrgent}
              setChecked={setIsUrgent}
              checkboxId="proofread-urgent-checkbox"
              data-testid="proofread-modal-urgent-toggle"
            />
            <ToggleLabel
              label={
                customer?.config?.translation_request_urgent_message ||
                "Urgent Delivery"
              }
              checked={isUrgent}
              checkboxId="proofread-urgent-checkbox"
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Actions>
        <Button
          onClick={(): void => dialogRef.current?.close()}
          data-testid="proofread-modal-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          loading={isSubmitting}
          disabled={!canSubmit}
          data-testid="proofread-modal-submit"
        >
          Send to proofreading
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
